import { useState } from 'react';
import { ChevronDown, Heart, MessageCircle, MoreHorizontal, Share } from 'lucide-react';
import { Avatar, Button, DropdownMenu, type DropdownMenuItem } from '@/components/ui';
import { useAppUser } from '@/lib/useAppUser';
import { cn } from '@/lib/utils';
import { formatNotificationTime } from '@/lib/notifications/format-relative-time';
import { useCommentReplies, useDeleteComment, useReportComment, useToggleCommentLike } from '@/lib/comments/api';
import type { Comment, CommentReportReason } from '@/lib/comments/types';
import { CommentComposer } from './CommentComposer';
import { commentErrorMessage } from './comment-errors';

type MenuAction = 'report-SPAM' | 'report-ABUSE' | 'report-MISLEADING' | 'delete';

export interface CommentItemProps {
  comment: Comment;
  eventId: string;
  /** Internal user id of the signed-in caller, or null when signed out. */
  currentUserId: string | null;
  /** Comment ids the caller has liked (from `/comments/me`). */
  likedIds: ReadonlySet<string>;
  /** Replies render slimmer and can't be replied to (single-level threading). */
  isReply?: boolean;
}

/** Placeholder copy for soft-deleted / moderated rows kept for thread structure. */
const PLACEHOLDER_TEXT: Partial<Record<Comment['status'], string>> = {
  USER_DELETED: 'Comment deleted by its author',
  HIDDEN: 'Comment hidden by moderation',
};

/** Shared look of the meta-row affordances (reply bubble, like, share). */
const META_ACTION = 'inline-flex items-center gap-1 text-xs text-muted transition-colors';

/**
 * One comment row (Figma 1727-78418 "Comments/Comment"): avatar, author/time,
 * body, then a meta row — reply bubble with count, like heart with count,
 * share — plus the report/delete overflow menu. Replies are collapsed by
 * default behind a "View Reply ⌄" toggle that lazy-loads the full thread.
 */
export function CommentItem({ comment, eventId, currentUserId, likedIds, isReply = false }: CommentItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [replying, setReplying] = useState(false);
  const [threadOpen, setThreadOpen] = useState(false);
  const [reported, setReported] = useState(false);
  const [shared, setShared] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { mutate: toggleLike, isPending: likePending } = useToggleCommentLike();
  const { mutate: deleteComment } = useDeleteComment({
    mutation: { onError: () => setActionError('Could not delete the comment — try again.') },
  });
  const { mutate: report } = useReportComment({
    mutation: {
      onSuccess: () => setReported(true),
      onError: (err) => setActionError(commentErrorMessage(err)),
    },
  });
  const thread = useCommentReplies(comment.id, threadOpen);

  const profile = useAppUser();

  const placeholder = comment.body === null;
  const isMine = currentUserId !== null && comment.author?.id === currentUserId;
  const liked = likedIds.has(comment.id);
  // Username only — never the legal display name (privacy in a public feed).
  const authorName = comment.author?.username ?? 'Player';
  // Same avatar as the sidebar: the caller's profile image on their own rows;
  // other authors have no stored image, so the shared silhouette fallback renders.
  const avatarSrc = isMine ? profile?.profileImage : undefined;

  const menuItems: DropdownMenuItem<MenuAction>[] = isMine
    ? [{ value: 'delete', label: 'Delete comment' }]
    : [
        { value: 'report-SPAM', label: 'Report spam' },
        { value: 'report-ABUSE', label: 'Report abuse' },
        { value: 'report-MISLEADING', label: 'Report misleading' },
      ];

  const onMenuSelect = (action: MenuAction) => {
    setMenuOpen(false);
    setActionError(null);
    if (action === 'delete') {
      deleteComment({ eventId, commentId: comment.id, parentId: comment.parentId });
      return;
    }
    report({ commentId: comment.id, reason: action.replace('report-', '') as CommentReportReason });
  };

  // No per-comment permalinks yet — sharing hands off the event page URL.
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ url });
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      }
    } catch {
      // Cancelled the share sheet / clipboard denied — nothing to surface.
    }
  };

  // Replies are collapsed behind "View Reply" — opening lazy-loads the full
  // thread, with the embedded preview shown until the first page arrives.
  const embedded = comment.replies ?? [];
  const loadedReplies = thread.data?.pages.flatMap((p) => p.items);
  const visibleReplies = threadOpen ? (loadedReplies ?? embedded) : [];

  return (
    <div className={cn('flex gap-2.5', isReply && 'pl-10')}>
      <Avatar size="sm" src={avatarSrc} alt={placeholder ? '' : authorName} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          {placeholder ? (
            <span className="text-sm italic text-muted">{PLACEHOLDER_TEXT[comment.status] ?? 'Comment unavailable'}</span>
          ) : (
            <>
              <span className="truncate text-sm font-medium text-primary">{authorName}</span>
              <span className="shrink-0 text-xs text-muted">{formatNotificationTime(comment.createdAt)}</span>
            </>
          )}
        </div>

        {!placeholder && <p className="whitespace-pre-wrap break-words text-sm text-primary">{comment.body}</p>}

        {!placeholder && (
          <div className="flex items-center gap-4">
            {/* Reply only on top-level comments — threading is strictly one
                level, so reply rows offer no reply affordance. (The api would
                re-parent a reply-to-reply anyway; the UI simply doesn't offer it.) */}
            {!isReply &&
              (currentUserId ? (
                <button
                  type="button"
                  onClick={() => setReplying((r) => !r)}
                  className={cn(META_ACTION, 'cursor-pointer hover:text-primary')}
                  aria-label="Reply"
                >
                  <MessageCircle className="size-4" />
                  {comment.replyCount > 0 && <span>{comment.replyCount}</span>}
                </button>
              ) : (
                comment.replyCount > 0 && (
                  <span className={META_ACTION}>
                    <MessageCircle className="size-4" />
                    {comment.replyCount}
                  </span>
                )
              ))}

            <button
              type="button"
              disabled={!currentUserId || likePending}
              onClick={() => toggleLike({ eventId, commentId: comment.id, liked: !liked, parentId: comment.parentId })}
              className={cn(
                META_ACTION,
                'cursor-pointer',
                liked ? 'text-danger' : 'hover:text-primary',
                !currentUserId && 'cursor-default',
              )}
              aria-label={liked ? 'Unlike comment' : 'Like comment'}
              aria-pressed={liked}
            >
              <Heart className={cn('size-4', liked && 'fill-current')} />
              {comment.likeCount > 0 && <span>{comment.likeCount}</span>}
            </button>

            <button
              type="button"
              onClick={() => void share()}
              className={cn(META_ACTION, 'cursor-pointer hover:text-primary')}
              aria-label="Share comment"
            >
              <Share className="size-4" />
              {shared && <span>Link copied</span>}
            </button>

            {reported ? (
              <span className="text-xs text-muted">Reported — thanks</span>
            ) : (
              currentUserId && (
                <DropdownMenu<MenuAction>
                  open={menuOpen}
                  onClose={() => setMenuOpen(false)}
                  anchor={
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6 text-muted"
                      onClick={() => setMenuOpen((o) => !o)}
                      aria-label="Comment actions"
                    >
                      <MoreHorizontal className="size-4" />
                    </Button>
                  }
                  items={menuItems}
                  value={'' as MenuAction}
                  onSelect={onMenuSelect}
                />
              )
            )}
          </div>
        )}

        {actionError && <p className="text-xs text-danger">{actionError}</p>}

        {replying && (
          <div className="pt-1">
            <CommentComposer
              eventId={eventId}
              parentId={comment.id}
              placeholder={`Reply to ${authorName}…`}
              autoFocus
              onPosted={() => {
                setReplying(false);
                // Surface the just-posted reply instead of leaving it hidden
                // behind a collapsed thread.
                setThreadOpen(true);
              }}
            />
          </div>
        )}

        {comment.replyCount > 0 && (
          <button
            type="button"
            onClick={() => setThreadOpen((o) => !o)}
            className="inline-flex cursor-pointer items-center gap-1 self-start pt-1 text-xs font-medium text-muted transition-colors hover:text-primary"
          >
            {threadOpen ? 'Hide' : 'View'} {comment.replyCount === 1 ? 'Reply' : 'Replies'}
            <ChevronDown className={cn('size-3.5 transition-transform', threadOpen && 'rotate-180')} />
          </button>
        )}

        {visibleReplies.length > 0 && (
          <div className="flex flex-col gap-3 pt-2">
            {visibleReplies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                eventId={eventId}
                currentUserId={currentUserId}
                likedIds={likedIds}
                isReply
              />
            ))}
          </div>
        )}

        {threadOpen && thread.hasNextPage && (
          <button
            type="button"
            onClick={() => void thread.fetchNextPage()}
            disabled={thread.isFetchingNextPage}
            className="cursor-pointer self-start pt-1 text-xs font-medium text-muted transition-colors hover:text-primary disabled:opacity-50"
          >
            {thread.isFetchingNextPage ? 'Loading…' : 'Show more replies'}
          </button>
        )}
      </div>
    </div>
  );
}
